import { createAsyncThunk } from '@reduxjs/toolkit';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import merge from 'lodash/merge';

import { selectCurrentThread, selectModuleSpecsMap, selectTypeSpecs, setTypeSpecs } from './threadsUiSlice';
import { flattenSteps } from './utils';
import SidekickModuleSpec, { Type, TypeSpec } from 'src/models/Sidekick/SidekickModuleSpec';
import SidekickThread from 'src/models/Sidekick/SidekickThreads';

interface NamedTypeSpec {
  name: string;
  type: TypeSpec;
}

export const getTypeSpecs = createAsyncThunk(
  'threadUi/getTypeSpecs',
  async (_, { dispatch, getState, extra: { gateways } }: any) => {
    const customerId = getState().sidekickConfiguration.value.v1CustomerId;

    const typeSpecs = getTypeSpecsFn(getState());
    const promises: Promise<NamedTypeSpec[]>[] = [];
    for (const typeName in typeSpecs) {
      if (!isNil(typeSpecs[typeName])) {
        continue;
      }

      promises.push(getTypeSpecsForType(typeName, customerId, gateways) as Promise<NamedTypeSpec[]>);
    }

    // Define built-ins.
    typeSpecs['string'] = { type: Type.string };
    typeSpecs['number'] = { type: Type.number };
    typeSpecs['bool'] = { type: Type.bool };
    typeSpecs['timestamp'] = { type: Type.timestamp };
    typeSpecs['array'] = { type: Type.array };
    typeSpecs['currency'] = { type: Type.currency };

    const result = await Promise.all(promises).then((namedSpecGroups: NamedTypeSpec[][]): void => {
      for (const namedSpecs of namedSpecGroups) {
        for (const spec of namedSpecs) {
          typeSpecs[spec.name] = spec.type;
        }
      }
      return typeSpecs;
    });
    dispatch(setTypeSpecs(result));
  }
);

const getTypeSpecsFn = (state): any | never => {
  const thread = selectCurrentThread(state) as SidekickThread;
  const specsMap = selectModuleSpecsMap(state);

  const typeSpecs = selectTypeSpecs(state);

  const typeSet: Record<string, unknown> = merge(
    { Classification: null, Classifications: null, Ticket: null, DateTime: null, Channel: null, AgentAssist: null },
    typeSpecs
  );

  const mods = flattenSteps(thread.nextData);

  let currentModSpec: SidekickModuleSpec | null = null;
  for (const nm of mods) {
    let spec = specsMap[nm.mod.name];

    if (spec) {
      currentModSpec = spec as SidekickModuleSpec;
    }

    if (!currentModSpec) {
      throw new Error(`null current mod spec on ${nm.mod.name}`);
    }

    if (!spec) {
      spec = find(currentModSpec.modifiers, modifierSpec => modifierSpec.name === nm.mod.name);

      if (!spec) {
        continue;
      }
    }

    if (spec.outMemories == null) {
      forEach((spec as SidekickModuleSpec)?.externalAction?.results || [], res => {
        if (res.name) {
          typeSet[res.name] = res.typeSpec;
        }
      });

      continue;
    }

    forEach(Object.values(spec.outMemories), memName => {
      typeSet[memName as string] = typeSpecs[memName as string] || null;
    });
  }

  return typeSet;
};

const getTypeSpecsForType = async (
  typeName: string,
  customerId: string,
  gateways: { sidekickTypeSpecs }
): Promise<unknown> => {
  const tp = baseType(typeName);
  switch (tp) {
    case 'string':
    case 'number':
    case 'bool':
    case 'timestamp':
    case 'array':
      return Promise.resolve([{ name: typeName, type: { type: typeName as Type } }]);
  }

  return gateways.sidekickTypeSpecs.fetch({ id: typeName, customerId }).then(result => {
    return map(result.data, (type, name) => ({ name, type }));
  });
};

// baseType strips type annotations down to their base form, e.g. MyType<X>
// becomes MyType.
export const baseType = (type: Type | string): string => {
  const idx = type.indexOf('<');
  if (idx === -1) {
    return type;
  }
  return type.substr(0, idx);
};
